import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { helpers, types } from 'helpers';

const TXN_LIMIT = 25;
const VOL_LIMIT = 5000;

interface Props {
    business: types.Business;
}

const FastOnboardBanner = ({ business }: Props) => {
    const navigate = useNavigate();

    if (business.preferences?.source !== 'fast') return null;

    const txnUsed = business.preferences?.transactions_total ?? 0;
    const volUsed = business.preferences?.transactions_volume ?? 0;
    const txnPct = Math.min((txnUsed / TXN_LIMIT) * 100, 100);
    const volPct = Math.min((volUsed / VOL_LIMIT) * 100, 100);
    const isBlocked = txnUsed >= TXN_LIMIT || volUsed >= VOL_LIMIT;
    const isNearLimit = !isBlocked && (txnPct >= 80 || volPct >= 80);

    const borderColor = isBlocked ? 'border-red-200' : isNearLimit ? 'border-amber-200' : 'border-blue-200';
    const bgColor = isBlocked ? 'bg-red-50' : isNearLimit ? 'bg-amber-50' : 'bg-blue-50';
    const textColor = isBlocked ? 'text-red-700' : isNearLimit ? 'text-amber-700' : 'text-blue-700';
    const barColor = isBlocked ? 'bg-red-500' : isNearLimit ? 'bg-amber-400' : 'bg-blue-500';

    return (
        <div className={`rounded-xl border p-4 mb-4 ${bgColor} ${borderColor}`}>
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className={`font-semibold text-sm mb-1 ${textColor}`}>
                        {isBlocked
                            ? 'Transaction limit reached — complete full KYB to continue'
                            : isNearLimit
                            ? 'Approaching fast onboard limit — complete full KYB soon'
                            : 'Fast Onboarded Account'}
                    </div>
                    <div className="text-xs text-gray-500 mb-3">
                        {isBlocked
                            ? 'Your account has reached its fast onboard limits. Complete full verification to keep transacting.'
                            : 'Your account has temporary transaction limits. Complete full onboarding to remove them.'}
                    </div>

                    <div className="flex gap-8 flex-wrap">
                        <div className="w-2/12">
                            <div className="flex justify-between gap-2 text-xs mb-1">
                                <span className="text-gray-500">Transactions</span>
                                <span className={`font-medium ${textColor}`}>{txnUsed} / {TXN_LIMIT}</span>
                            </div>
                            <div className="bg-gray-200 rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${txnPct}%` }} />
                            </div>
                        </div>
                        <div className="w-3/12">
                            <div className="flex justify-between gap-2 text-xs mb-1">
                                <span className="text-gray-500">Volume</span>
                                <span className={`font-medium ${textColor}`}>
                                    GHS {helpers.format.number(volUsed)} / GHS {helpers.format.number(VOL_LIMIT)}
                                </span>
                            </div>
                            <div className="bg-gray-200 rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${volPct}%` }} />
                            </div>
                        </div>
                    </div>
                </div>

                <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate('/settings/business')}
                    className={isBlocked ? 'bg-red-600 border-red-600' : isNearLimit ? 'bg-amber-500 border-amber-500' : ''}
                >
                    Complete Full KYB
                </Button>
            </div>
        </div>
    );
};

export default FastOnboardBanner;
